import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Trash2, Plus, Search, Pencil, RefreshCw } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';

export default function ManageUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        fullName: '',
        phone: '',
        role: 'customer',
        vehicleType: 'bike',
        vehicleNumber: '',
        licenseNumber: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [activeRoleTab, setActiveRoleTab] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [confirmDelete, setConfirmDelete] = useState({ open: false, userId: null, userName: '' });
    const [isEditing, setIsEditing] = useState(false);
    const [editUserId, setEditUserId] = useState(null);
    const toast = useToast();
    const { user: currentUser, setAuth, token: currentToken } = useAuth();

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await api.get('/api/auth/admin/users');
            setUsers(res.data);
        } catch (err) {
            console.error('Failed to fetch users', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await fetchUsers();
            toast.success('Users and staff refreshed successfully');
        } catch (err) {
            toast.error('Failed to refresh users');
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleSaveUser = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setSubmitting(true);
        try {
            if (isEditing) {
                const res = await api.put(`/api/auth/admin/users/${editUserId}`, formData);
                toast.success('User updated successfully');
                
                // If editing self, update the local auth state
                if (editUserId === currentUser?.id) {
                    setAuth(currentToken, { ...currentUser, ...res.data.user });
                }
            } else {
                await api.post('/api/auth/admin/users', formData);
                toast.success('User created successfully');
            }
            setFormData({ email: '', password: '', fullName: '', phone: '', role: 'customer', vehicleType: 'bike', vehicleNumber: '', licenseNumber: '' });
            fetchUsers();
            setShowAddForm(false);
            setIsEditing(false);
            setEditUserId(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Error processing user');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditUser = (user) => {
        setFormData({
            email: user.email,
            password: '', // Leave blank unless changing
            fullName: user.full_name,
            phone: user.phone || '',
            role: user.role === 'admin' ? 'restaurant_admin' : user.role,
            vehicleType: user.vehicle_type || 'bike',
            vehicleNumber: user.vehicle_number || '',
            licenseNumber: user.license_number || ''
        });
        setEditUserId(user.id);
        setIsEditing(true);
        setShowAddForm(true);
    };

    const handleDeleteUser = async (userId, role) => {
        if (role === 'admin' || role === 'restaurant_admin') {
            toast.error('Cannot delete admin users.');
            return;
        }
        const user = users.find(u => u.id === userId);
        setConfirmDelete({ open: true, userId, userName: user?.full_name || 'this user' });
    };

    const confirmDeleteUser = async () => {
        try {
            await api.delete(`/api/auth/admin/users/${confirmDelete.userId}`);
            toast.success('User deleted successfully');
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error deleting user');
        } finally {
            setConfirmDelete({ open: false, userId: null, userName: '' });
        }
    };

    if (loading) return <Loading mode="page" message="Loading staff and customers..." />;

    const roleColors = {
        admin: 'bg-red-100 text-red-800',
        restaurant_admin: 'bg-red-100 text-red-800',
        kitchen_staff: 'bg-orange-100 text-orange-800',
        delivery_partner: 'bg-blue-100 text-blue-800',
        customer: 'bg-gray-100 text-gray-800'
    };

    const roleTabs = [
        { key: 'all', label: 'All' },
        { key: 'restaurant_admin', label: 'Admin' },
        { key: 'kitchen_staff', label: 'Kitchen Staff' },
        { key: 'delivery_partner', label: 'Delivery' },
        { key: 'customer', label: 'Customers' },
    ];

    const filteredUsers = (activeRoleTab === 'all' 
        ? users 
        : users.filter(u => {
            if (activeRoleTab === 'restaurant_admin') {
                return u.role === 'restaurant_admin' || u.role === 'admin';
            }
            return u.role === activeRoleTab;
        })
    ).filter(u => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return (u.full_name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || u.phone?.includes(s));
    });

    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const paginatedUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="space-y-6">
            <ConfirmModal
                isOpen={confirmDelete.open}
                title="Delete User"
                message={`Are you sure you want to delete "${confirmDelete.userName}"? This action cannot be undone.`}
                confirmText="Delete"
                onConfirm={confirmDeleteUser}
                onCancel={() => setConfirmDelete({ open: false, userId: null, userName: '' })}
            />

            <div className="flex justify-between items-center gap-4">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900">Manage Users & Staff</h1>
                    <button
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="p-2 text-gray-600 hover:text-primary-600 bg-white border border-gray-200 rounded-xl hover:border-primary-300 shadow-sm transition-all flex items-center justify-center disabled:opacity-50"
                        title="Refresh Users & Staff"
                    >
                        <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin text-primary-600' : ''}`} />
                    </button>
                </div>
                {!showAddForm && (
                    <button
                        onClick={() => setShowAddForm(true)}
                        className="btn-primary flex items-center shadow-lg"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Add New User
                    </button>
                )}
            </div>

            {showAddForm && (
                <div className="card p-6 border-2 border-primary-100">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-bold">Create New User</h2>
                        <button
                            onClick={() => {
                                setShowAddForm(false);
                                setIsEditing(false);
                                setEditUserId(null);
                                setFormData({ email: '', password: '', fullName: '', phone: '', role: 'customer', vehicleType: 'bike', vehicleNumber: '', licenseNumber: '' });
                            }}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            Cancel
                        </button>
                    </div>
                    {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4">{error}</div>}
                    {success && <div className="bg-green-50 text-green-600 p-3 rounded-lg mb-4">{success}</div>}

                    <form onSubmit={handleSaveUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                            <input 
                                type="text" 
                                required 
                                disabled={isEditing && editUserId === currentUser?.id}
                                className={`w-full px-4 py-2 border rounded-lg ${isEditing && editUserId === currentUser?.id ? 'bg-gray-50 text-gray-500' : ''}`} 
                                value={formData.fullName} 
                                onChange={e => setFormData({ ...formData, fullName: e.target.value })} 
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input type="email" required disabled={isEditing} className="w-full px-4 py-2 border rounded-lg bg-gray-50 disabled:text-gray-500" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                            {isEditing && <p className="text-[10px] text-gray-400 mt-1">Email cannot be changed</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <input type="tel" required className="w-full px-4 py-2 border rounded-lg" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{isEditing ? 'New Password (Optional)' : 'Given Password'}</label>
                            <input 
                                type="password" 
                                required={!isEditing} 
                                disabled={isEditing && editUserId === currentUser?.id}
                                className={`w-full px-4 py-2 border rounded-lg ${isEditing && editUserId === currentUser?.id ? 'bg-gray-50 text-gray-500' : ''}`} 
                                value={formData.password} 
                                onChange={e => setFormData({ ...formData, password: e.target.value })} 
                            />
                            {isEditing && ! (editUserId === currentUser?.id) && <p className="text-[10px] text-gray-400 mt-1">Leave blank to keep current password</p>}
                            {isEditing && editUserId === currentUser?.id && <p className="text-[10px] text-gray-400 mt-1">Password must be changed via Reset Password flow</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                            <select 
                                className={`w-full px-4 py-2 border rounded-lg ${isEditing && editUserId === currentUser?.id ? 'bg-gray-50 text-gray-500' : ''}`} 
                                disabled={isEditing && editUserId === currentUser?.id}
                                value={formData.role} 
                                onChange={e => setFormData({ ...formData, role: e.target.value })}
                            >
                                <option value="customer">Customer</option>
                                <option value="kitchen_staff">Kitchen Staff</option>
                                <option value="delivery_partner">Delivery Partner</option>
                                <option value="restaurant_admin">Admin</option>
                            </select>
                        </div>
                        {formData.role === 'delivery_partner' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                                    <select className="w-full px-4 py-2 border rounded-lg" value={formData.vehicleType} onChange={e => setFormData({ ...formData, vehicleType: e.target.value })}>
                                        <option value="bike">Bike</option>
                                        <option value="scooter">Scooter</option>
                                        <option value="car">Car</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number</label>
                                    <input type="text" required className="w-full px-4 py-2 border rounded-lg" value={formData.vehicleNumber} onChange={e => setFormData({ ...formData, vehicleNumber: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                                    <input type="text" required className="w-full px-4 py-2 border rounded-lg" value={formData.licenseNumber} onChange={e => setFormData({ ...formData, licenseNumber: e.target.value })} />
                                </div>
                            </>
                        )}
                        <div className="md:col-span-2 mt-2">
                            <button type="submit" disabled={submitting} className="btn-primary w-full md:w-auto min-w-[150px] flex justify-center items-center">
                                {submitting ? (
                                    <Loading mode="button" message={isEditing ? 'Updating...' : 'Creating...'} />
                                ) : (
                                    isEditing ? 'Update User' : 'Create User'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Role Filter Tabs */}
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-full overflow-x-auto whitespace-nowrap hide-scrollbar max-w-full">
                {roleTabs.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveRoleTab(tab.key)}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                            activeRoleTab === tab.key
                                ? 'bg-white text-primary-700 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        {tab.label}
                        <span className="ml-1.5 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">
                            {tab.key === 'all' 
                                ? users.length 
                                : users.filter(u => tab.key === 'restaurant_admin' ? (u.role === 'restaurant_admin' || u.role === 'admin') : u.role === tab.key).length
                            }
                        </span>
                    </button>
                ))}
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search by name, email, or phone..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                />
            </div>

            <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedUsers.map(u => (
                            <tr key={u.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{u.full_name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.phone || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors[u.role] || roleColors['customer']}`}>
                                        {u.role.replace('_', ' ').toUpperCase()}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                                    <button 
                                        onClick={() => handleEditUser(u)} 
                                        className="text-primary-600 hover:text-primary-900"
                                        title="Edit User"
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button 
                                        onClick={() => handleDeleteUser(u.id, u.role)} 
                                        className={`text-red-600 hover:text-red-900 ${currentUser?.id === u.id ? 'opacity-30 cursor-not-allowed' : ''}`}
                                        title={currentUser?.id === u.id ? "Cannot delete yourself" : "Delete User"}
                                        disabled={currentUser?.id === u.id}
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </td>
                            </tr>
                        ))
                        }
                        {paginatedUsers.length === 0 && (
                            <tr>
                                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                    No users found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                </div>
                <div className="flex justify-between items-center px-6 py-4 border-t bg-gray-50">
                    <div className="text-sm text-gray-500">
                        {filteredUsers.length === 0
                            ? 'No users found'
                            : <>Showing <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredUsers.length)}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredUsers.length)}</span> of <span className="font-medium">{filteredUsers.length}</span></>
                        }
                    </div>
                    <div className="flex space-x-2">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="px-3 py-1 border rounded bg-white text-gray-600 disabled:opacity-40 hover:bg-gray-100">Previous</button>
                        <span className="px-3 py-1 text-sm text-gray-600">Page {currentPage} of {Math.max(totalPages, 1)}</span>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} className="px-3 py-1 border rounded bg-white text-gray-600 disabled:opacity-40 hover:bg-gray-100">Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
